import { log } from '@/log'
import { CartItem } from '@/models/cart/cart-item'
import { CartGroup, isDisplayItem } from '@/models/cart/group/group'
import _ from 'lodash'
import { GetterTree } from 'vuex'

import { RootState } from '../../types'
import { filterValidItemAsAggregate, totalCountInScope } from './function'
import { CartState } from './types'

type ItemFilter = (item: CartItem) => boolean

/**
 * FIXME:
 * https://github.com/my-color/front/pull/465#issuecomment-460920398
 * https://github.com/my-color/front/pull/465#issuecomment-460921912
 * https://github.com/my-color/front/pull/465#issuecomment-460932320
 *
 * ブランドごとの商品絞り込みに対応するためにxxxWithFilterというgetterを定義しているが、
 * 外部のコンテキストに従ってデータを取得するという時点で、storeのgetterとして定義するのは不適切かもしれない.
 *
 * p2.2.0では、取り急ぎ動くところまで持っていくことを優先して既存構造に乗っかるような形にしてしまっているが、
 * 抜本的にはstoreの構造自体を現状に合わせて回収する必要が有ると思われる.
 */

export const getters: GetterTree<CartState, RootState> = {
  hasError(state): boolean {
    return _.has(state, 'error.error')
  },

  getError(state): Error {
    return _.get(state, 'error.error')
  },

  getItem(_state, getter): (string) => CartItem | undefined {
    return (id: string) => {
      return _.find(getter.currentItems, ['id', id])
    }
  },

  currentItems(state: CartState): CartItem[] {
    const currentScope = state.currentScope

    if (currentScope === null) {
      log.info(`current scope is not assigned, so return all cart items as currentItems`)

      return state.groups.items().list
    }

    return state.groups.findByScope(currentScope).foldL(
      (): CartItem[] => {
        log.info(`not found group for scope(${currentScope.kind.toString()}.${currentScope.value.toString()})`)

        return []
      },
      (group: CartGroup) => group.items.list
    )
  },
  /**
   * 有効(ステータスや在庫数)なアイテムを返す。
   * 修正前まではchildを含まないものだったが、いまはchildを含むものになっている。
   * childを含まないものは、displayItemsを使うか、自前でフィルタすること。
   */
  items(_state, getter): CartItem[] {
    return getter.currentItems.filter(item => filterValidItemAsAggregate(item, getter.currentItems))
  },

  /**
   * 有効(ステータスや在庫数)なアイテムを返す。
   * 修正前まではchildを含まないものだったが、いまはchildを含むものになっている。
   * childを含まないものは、displayItemsを使うか、自前でフィルタすること。
   *
   * TODO: 絞り込みを一元化
   * this.$store.getters['cart/itemsWithFilter'](this.isPurchasableCartItem) といった絞り込みが
   * 各種 page コンポーネントで都度再実装されてしまっている.
   * 結果、知識の分散化と「同じカート内商品情報に基づいて表示している」ということが担保できなくなっているので、
   * 何らかの形で一元化したい.
   *
   * 候補としては
   * - order-context#filter に取り込む
   * - store の getter に取り込む
   *   - 併せて、重複している getter の削除や統合などが必要になりそう
   * などが考えられそうか.
   */
  itemsWithFilter(_state, getter): (filter: ItemFilter) => CartItem[] {
    return (filter: (item: CartItem) => boolean) =>
      getter.currentItems.filter(item => filterValidItemAsAggregate(item, getter.currentItems)).filter(filter)
  },

  displayItems(_state, getter): CartItem[] {
    return getter.currentItems
      .filter(item => filterValidItemAsAggregate(item, getter.currentItems))
      .filter(item => isDisplayItem(item))
  },

  displayItemsWithFilter(_state, getter): (filter: ItemFilter) => CartItem[] {
    return (filter: ItemFilter) =>
      getter.currentItems
        .filter(item => filterValidItemAsAggregate(item, getter.currentItems))
        .filter(item => isDisplayItem(item)).filter(filter)
  },

  noveltyItems(_state, getter): CartItem[] {
    return getter.currentItems.filter(item => item.isNovelty)
  },

  noveltyItemsWithFilter(_state, getter): (filter: ItemFilter) => CartItem[] {
    return (filter: ItemFilter) => getter.currentItems.filter(item => item.isNovelty).filter(filter)
  },

  isFreeShippingOnCatalogOnly(_state, getter): boolean {
    return _.every(getter.currentItems, 'isCatalogType')
  },
  // :pensive:
  isFreeShippingOnCatalogOnlyWithFilter(_state, getter): (filter: ItemFilter) => boolean {
    return (filter: ItemFilter) => _.every(
      (getter.currentItems.filter(filter)), 'isCatalogType'
    )
  },

  /**
   * Check the number both of parent and child root are the same.
   * This is a simple workaround for incomplete hidden set data.
   * TODO: When a more robust way is implemented, this will be done away with.
   */
  isHiddenSetParityIsValid(_state, getter): boolean {
    const numOfParent = _.countBy(getter.currentItems, item => item.isParent)
    const numOfChild = _.countBy(getter.currentItems, item => item.isChild)

    return numOfParent === numOfChild
  },

  /**
   * 現在のスコープに基づいてカートの点数を返す
   * 優先順位:
   * 1. カートの詳細な情報（アイテムのリスト）があれば、それを優先して点数を数える
   * 2. なければstate.countから該当のブランドの点数を返す
   * 3. どちらもなければ0を返す
   */
  currentCartItemCount(state, getter): number {
    if (getter.currentItems.length > 0) {
      return totalCountInScope(getter.currentItems)
    }

    const { currentScope } = state

    // スコープが設定されていない場合はすべてのブランドの点数を足したものを返却する
    // これはcurrentItemsの挙動と平仄を合わせている
    if (!currentScope || !currentScope.kind.isBrand) {
      return state.count.reduce(
        (acc, c) => c.status === 'loaded' ? acc + c.value : acc,
        0
      )
    }

    const countWithBrand = state.count.find(c => c.brand === currentScope.value.toString())

    return countWithBrand && countWithBrand.status === 'loaded'
      ? countWithBrand.value
      : 0
  },
}
